/**
 * Done. / ChiroCPD — App Root
 *
 * Wouter router with 6 routes (contract maximum).
 * Route guard on "/" handles auth state → redirect.
 *
 * Routes:
 *   /               → RouteGuard (redirect by auth state)
 *   /onboarding     → OnboardingPage
 *   /dashboard      → DashboardPage
 *   /activities     → ActivitiesPage
 *   /export         → ExportPage (CRITICAL PATH)
 *   *               → 404 fallback
 */

import { Switch, Route } from 'wouter';
import { AuthProvider } from '@/contexts/AuthContext';
import { RouteGuard } from '@/pages/RouteGuard';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { ActivitiesPage } from '@/pages/ActivitiesPage';
import { ExportPage } from '@/pages/ExportPage';
import { LearnLibraryPage } from '@/pages/LearnLibraryPage';
import { ModulePage } from '@/pages/ModulePage';
import { AdminModulesPage } from '@/pages/AdminModulesPage';
import { AdminModuleNewPage, AdminModuleEditPage } from '@/pages/AdminModuleFormPage';
import { AdminUsersPage, AdminUserNewPage, AdminUserEditPage } from '@/pages/AdminUsersPage';
import { AdminPromptsPage } from '@/pages/AdminPromptsPage';
import { CertificatePage } from '@/pages/CertificatePage';
import { AccountPage } from '@/pages/AccountPage';
import { LoginPage } from '@/pages/LoginPage';
import { BrandFooter } from '@/components/ui/BrandFooter';

function NotFoundPage(): React.ReactElement {
  return (
    <div className="flex min-h-screen flex-col bg-[var(--surface-subtle)]">
      <main className="flex-1 flex flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="text-4xl font-bold text-[var(--text-primary)]">404</h1>
        <p className="text-[var(--text-secondary)]">Page not found.</p>
        <a
          href="/dashboard"
          className="text-sm font-medium text-[var(--color-done-600)] hover:underline"
        >
          → Go to Dashboard
        </a>
      </main>
      <BrandFooter />
    </div>
  );
}

export function App(): React.ReactElement {
  return (
    <AuthProvider>
      <Switch>
        <Route path="/" component={RouteGuard} />
        <Route path="/onboarding" component={OnboardingPage} />
        <Route path="/dashboard" component={DashboardPage} />
        <Route path="/activities" component={ActivitiesPage} />
        <Route path="/export" component={ExportPage} />
        <Route path="/learn" component={LearnLibraryPage} />
        <Route path="/modules/:id" component={ModulePage} />
        <Route path="/login" component={LoginPage} />
        <Route path="/admin/modules" component={AdminModulesPage} />
        <Route path="/admin/modules/new" component={AdminModuleNewPage} />
        <Route path="/admin/modules/:id/edit" component={AdminModuleEditPage} />
        <Route path="/admin/users" component={AdminUsersPage} />
        <Route path="/admin/users/new" component={AdminUserNewPage} />
        <Route path="/admin/users/:id/edit" component={AdminUserEditPage} />
        <Route path="/admin/prompts" component={AdminPromptsPage} />
        <Route path="/cert/:moduleId" component={CertificatePage} />
        <Route path="/account" component={AccountPage} />
        <Route component={NotFoundPage} />
      </Switch>
    </AuthProvider>
  );
}
